import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TwilioErrorExport from "@/components/reports/TwilioErrorExport";
import SmsSuppressionReport from "@/components/reports/sms-suppression/SmsSuppressionReport";
import { useAuth } from "@/contexts/AuthContext";
import {
    Download, RefreshCw, Loader2, Search, CheckCircle2, Send,
    Zap, XCircle, Ban, SkipForward, Clock, ChevronRight, AlertTriangle, Info,
    Voicemail, Bot, FileText,
} from "lucide-react";
import { toast } from "sonner";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
    CartesianGrid, LineChart, Line, Legend, PieChart, Pie, Cell,
} from "recharts";

// ── Vocabulary ─────────────────────────────────────────────────────

const TIMEFRAMES = [
    { v: "today", l: "Today" },
    { v: "yesterday", l: "Yesterday" },
    { v: "7d", l: "Last 7 Days" },
    { v: "30d", l: "Last 30 Days" },
    { v: "this_month", l: "This Month" },
    { v: "custom", l: "Custom" },
];

const SOURCES = [
    { v: "all", l: "All Sources" },
    { v: "campaign", l: "Lead Campaigns" },
    { v: "workflow", l: "Workflow Automations" },
    { v: "voicemail", l: "Voicemail Callbacks" },
    { v: "inbound", l: "Inbound SMS Replies" },
    { v: "inbound_unmatched", l: "Inbound Replies — Unmatched" },
    { v: "other", l: "Other" },
];

const STATUS_BUCKETS = [
    { v: "all", l: "All statuses" },
    { v: "delivered", l: "Delivered (carrier-confirmed)" },
    { v: "sent", l: "Sent (in transit)" },
    { v: "in_flight", l: "Accepted / In-flight" },
    { v: "failed", l: "Failed" },
    { v: "opted_out", l: "Opted Out" },
    { v: "skipped", l: "Skipped" },
    { v: "queued", l: "Queued (pre-dispatch)" },
    { v: "selected", l: "Selected (pre-dispatch)" },
];

const BUCKET_STYLES = {
    delivered: { color: "bg-emerald-100 text-emerald-800 ring-emerald-200", chart: "#10b981" },
    sent: { color: "bg-sky-100 text-sky-800 ring-sky-200", chart: "#0ea5e9" },
    in_flight: { color: "bg-indigo-100 text-indigo-800 ring-indigo-200", chart: "#6366f1" },
    failed: { color: "bg-rose-100 text-rose-800 ring-rose-200", chart: "#f43f5e" },
    opted_out: { color: "bg-amber-100 text-amber-900 ring-amber-200", chart: "#f59e0b" },
    skipped: { color: "bg-slate-100 text-slate-700 ring-slate-200", chart: "#64748b" },
    queued: { color: "bg-violet-100 text-violet-800 ring-violet-200", chart: "#8b5cf6" },
    selected: { color: "bg-fuchsia-100 text-fuchsia-800 ring-fuchsia-200", chart: "#c026d3" },
    unknown: { color: "bg-gray-100 text-gray-600 ring-gray-200", chart: "#9ca3af" },
};

function StatusPill({ bucket }) {
    const cfg = BUCKET_STYLES[bucket] || BUCKET_STYLES.unknown;
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-wide ring-1 ${cfg.color}`}
            data-testid={`row-bucket-${bucket}`}
        >
            {bucket.replace(/_/g, " ")}
        </span>
    );
}

function SummaryCard({ icon: Icon, label, value, sub, tone, testId }) {
    const toneClass = {
        emerald: "border-l-4 border-emerald-500 bg-emerald-50/40",
        sky: "border-l-4 border-sky-500 bg-sky-50/40",
        indigo: "border-l-4 border-indigo-500 bg-indigo-50/40",
        rose: "border-l-4 border-rose-500 bg-rose-50/40",
        amber: "border-l-4 border-amber-500 bg-amber-50/40",
        slate: "border-l-4 border-slate-500 bg-slate-50/40",
        primary: "border-l-4 border-primary bg-primary/5",
    }[tone] || "border-l-4 border-gray-300 bg-white";
    return (
        <div
            className={`rounded-xl p-4 border border-border shadow-sm ${toneClass}`}
            data-testid={testId}
        >
            <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
                {Icon ? <Icon className="h-4 w-4 text-muted-foreground" /> : null}
            </div>
            <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
            {sub ? <div className="text-xs text-muted-foreground mt-0.5">{sub}</div> : null}
        </div>
    );
}

const PT_FMT = { timeZone: "America/Los_Angeles", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };

const SAVED_VIEWS = [
    { id: "overnight", l: "Overnight Replies", tf: "overnight", src: "inbound" },
    { id: "overnight-unmatched", l: "Overnight Unmatched", tf: "overnight", src: "inbound_unmatched" },
    { id: "all-unmatched", l: "All Unmatched Replies (7 days)", tf: "7d", src: "inbound_unmatched" },
];

function SavedViews({ timeframe, source, setTimeframe, setSource }) {
    return (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="reports-saved-views">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mr-1">Saved views</span>
            {SAVED_VIEWS.map(v => {
                const active = timeframe === v.tf && source === v.src;
                return (
                    <button
                        key={v.id}
                        data-testid={`saved-view-${v.id}`}
                        onClick={() => { setSource(v.src); setTimeframe(v.tf); }}
                        className={"px-3 py-1 text-xs rounded-full border transition-colors " + (active
                            ? "bg-emerald-700 text-white border-emerald-700"
                            : "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100")}
                    >{v.l}</button>
                );
            })}
        </div>
    );
}

function OvernightWindowNote({ start, end }) {
    const f = (iso) => new Date(iso).toLocaleString("en-US", PT_FMT);
    return (
        <div className="text-xs text-gray-600 bg-gray-50 border border-border rounded px-3 py-2" data-testid="reports-overnight-window">
            Overnight window (voicemail schedule, Mon–Fri 8 AM–3 PM PT): <span className="font-medium">{f(start)}</span> →{" "}
            <span className="font-medium">{f(end)}</span> PT
        </div>
    );
}

function InboundStatus({ row }) {
    return (
        <div className="flex flex-col items-start gap-1">
            <span
                className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-wide ring-1 bg-emerald-100 text-emerald-800 ring-emerald-200"
                data-testid="row-bucket-received"
            >
                received
            </span>
            {row.inbound_match_status === "unmatched" ? (
                <span
                    className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-rose-600 text-white"
                    data-testid="reports-row-unmatched-badge"
                    title="No Lead or Client has this sender number — needs staff review"
                >
                    unmatched
                </span>
            ) : null}
            {row.inbound_match_status === "staff" ? (
                <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-slate-700 text-white" data-testid="reports-row-staff-badge">
                    staff phone
                </span>
            ) : null}
            {row.notification_status ? (
                <span
                    className={"inline-block px-1.5 py-0.5 rounded text-[10px] font-medium " + (
                        row.notification_status === "sent" ? "bg-sky-50 text-sky-800"
                            : row.notification_status === "failed" ? "bg-rose-50 text-rose-800 ring-1 ring-rose-200"
                                : "bg-gray-100 text-gray-500")}
                    title={row.notification_reason || ""}
                    data-testid="reports-row-notification-status"
                >
                    {row.notification_status === "sent" ? "notified" : `notify ${row.notification_status}`}
                    {row.notification_status === "failed" && row.notification_reason ? `: ${row.notification_reason}` : ""}
                </span>
            ) : null}
            {row.opt_out_keyword ? (
                <span className="font-mono text-[10px] uppercase text-amber-800 bg-amber-50 rounded px-1.5 py-0.5" data-testid="reports-row-keyword">
                    {row.opt_out_keyword} keyword
                </span>
            ) : null}
        </div>
    );
}

const DRAFTER_REASON_LABELS = {
    ok: "LLM draft accepted",
    short_transcript: "transcript too short",
    llm_no_response: "LLM unavailable / error",
    llm_generic: "LLM judged no clear reason",
    "rejected:missing_after_hours": "validator rejected: no after-hours wording",
    "rejected:implies_missed_call": "validator rejected: implied a missed call",
    "rejected:missing_callback_time_request": "validator rejected: no callback-time request",
    "rejected:card_number_detected": "validator rejected: card-like number",
};

function DrafterMeta({ drafter, reason }) {
    const isLlm = drafter === "llm";
    const Icon = isLlm ? Bot : FileText;
    const reasonLabel = reason
        ? DRAFTER_REASON_LABELS[reason] || reason.replace(/^rejected:/, "validator rejected: ")
        : "";
    return (
        <div
            className="mt-1.5 pt-1.5 border-t border-dashed border-gray-200 flex flex-wrap items-center gap-1.5 select-none"
            data-testid="reports-row-drafter-meta"
        >
            <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-400">Drafted by</span>
            <span
                className={
                    "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] font-semibold uppercase " +
                    (isLlm ? "border-indigo-300 bg-indigo-600 text-white" : "border-amber-300 bg-amber-100 text-amber-900")
                }
                data-testid="reports-row-drafter"
                title={isLlm ? "SMS body written by the LLM drafter" : "SMS body generated by the deterministic fallback template"}
            >
                <Icon className="h-3 w-3" />
                {drafter}
            </span>
            {reasonLabel && !isLlm ? (
                <span className="text-[10px] italic text-gray-500" data-testid="reports-row-drafter-reason">
                    {reasonLabel}
                </span>
            ) : null}
        </div>
    );
}


export default function Reports() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    // Filters
    const [timeframe, setTimeframe] = useState("7d");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [source, setSource] = useState("all");
    const [campaignId, setCampaignId] = useState("all");
    const [workflowStatusId, setWorkflowStatusId] = useState("all");
    const [templateId, setTemplateId] = useState("all");
    const [statusBucket, setStatusBucket] = useState("all");
    const [errorCode, setErrorCode] = useState("all");
    const [search, setSearch] = useState("");

    // Data
    const [options, setOptions] = useState({
        campaigns: [], templates: [], workflow_statuses: [],
        error_codes: [], status_buckets: [], sources: [], timeframes: [],
    });
    const [summary, setSummary] = useState(null);
    const [charts, setCharts] = useState({ status_series: [], top_errors: [], daily_series: [] });
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/reports/sms-delivery/filter-options");
                setOptions(r.data);
            } catch (e) { toast.error(formatApiError(e)); }
        })();
    }, []);

    // Custom date range must be validated locally BEFORE any request fires.
    // Empty strings + `startDate <= endDate` (lexicographic works for YYYY-MM-DD).
    const customDatesValid = Boolean(startDate) && Boolean(endDate) && startDate <= endDate;

    const buildParams = useCallback(() => {
        const p = { timeframe };
        if (timeframe === "custom") {
            // Parse both endpoints as LOCAL time (consistent) so the user's
            // "Jan 1 – Jan 7" means their local calendar days, not a mix of
            // UTC-midnight (start) and local-time (end) as before.
            if (startDate) {
                p.start_date = new Date(`${startDate}T00:00:00`).toISOString();
            }
            if (endDate) {
                p.end_date = new Date(`${endDate}T23:59:59.999`).toISOString();
            }
        }
        if (source && source !== "all") p.source = source;
        if (campaignId && campaignId !== "all") p.campaign_id = campaignId;
        if (workflowStatusId && workflowStatusId !== "all") p.workflow_status_id = workflowStatusId;
        if (templateId && templateId !== "all") p.template_id = templateId;
        if (statusBucket && statusBucket !== "all") p.status_bucket = statusBucket;
        if (errorCode && errorCode !== "all") p.error_code = errorCode;
        if (search.trim()) p.search = search.trim();
        return p;
    }, [timeframe, startDate, endDate, source, campaignId, workflowStatusId, templateId, statusBucket, errorCode, search]);

    const load = useCallback(async () => {
        // Guard: never fire a custom-range request with missing/invalid dates
        // (this is what caused the prod failure — an unbounded custom query).
        if (timeframe === "custom" && !customDatesValid) return;
        setLoading(true);
        try {
            const params = buildParams();
            const { data } = await api.get("/reports/sms-delivery", { params });
            setSummary(data.summary || null);
            setCharts(data.charts || { status_series: [], top_errors: [], daily_series: [] });
            setRows(data.rows || []);
            setMeta(data.meta || null);
        } catch (e) {
            // Capture exact failure reason (URL + status) so root-cause is
            // visible in the toast and the browser console.
            const status = e?.response?.status;
            const url = e?.config ? `${e.config.baseURL || ""}${e.config.url || ""}` : "";
            const qs = e?.config?.params
                ? new URLSearchParams(e.config.params).toString()
                : "";
            const detail = formatApiError(e);
            // eslint-disable-next-line no-console
            console.error("[reports/sms-delivery] request failed", {
                status, url: qs ? `${url}?${qs}` : url, error: e,
            });
            toast.error(
                `${detail}${status ? ` (HTTP ${status})` : ""}`,
                { description: qs ? `${url}?${qs}` : url }
            );
            setSummary(null);
            setCharts({ status_series: [], top_errors: [], daily_series: [] });
            setRows([]);
            setMeta(null);
        } finally {
            // ALWAYS reset loading so a failed custom request never leaves the
            // page stuck — Today/7d/30d must remain immediately runnable.
            setLoading(false);
        }
    }, [buildParams, timeframe, customDatesValid]);

    // Auto-run for presets ONLY. Custom must be triggered explicitly via GO.
    useEffect(() => {
        if (timeframe === "custom") return;
        load();
        // We intentionally depend on `load` (which depends on all filters).
        // When switching FROM custom to a preset, `timeframe` changes and this
        // fires; when switching TO custom, we short-circuit above.
    }, [load, timeframe]);

    const downloadCSV = async () => {
        try {
            const qs = new URLSearchParams(buildParams()).toString();
            const token = getToken();
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/sms-delivery/export?${qs}`, {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error(`Export failed: ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `sms-delivery-${(summary?.range_start || "all").slice(0, 10)}-to-${(summary?.range_end || "now").slice(0, 10)}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch (e) { toast.error(e.message || "CSV export failed"); }
    };

    // Precomputed derived UI values
    const deliveryPercent = summary?.delivery_rate ?? 0;

    const statusChartData = useMemo(() => (
        (charts.status_series || []).map(s => ({
            name: s.bucket, count: s.count, fill: (BUCKET_STYLES[s.bucket] || BUCKET_STYLES.unknown).chart,
        }))
    ), [charts]);

    const topErrorsData = useMemo(() => (
        (charts.top_errors || []).map(e => ({ code: e.error_code, count: e.count }))
    ), [charts]);

    const dailySeries = charts.daily_series || [];

    return (
        <div className="space-y-6" data-testid="reports-page">
            <Tabs defaultValue="sms-delivery" className="w-full">
                <TabsList data-testid="reports-tabs">
                    <TabsTrigger value="sms-delivery" data-testid="tab-sms-delivery">SMS Delivery &amp; Errors</TabsTrigger>
                    <TabsTrigger value="twilio-export" data-testid="tab-twilio-export">
                        Twilio Error Export
                    </TabsTrigger>
                    {isAdmin && (
                        <TabsTrigger value="sms-suppression" data-testid="tab-sms-suppression">SMS Suppression</TabsTrigger>
                    )}
                </TabsList>
                <TabsContent value="twilio-export" className="mt-6">
                    <TwilioErrorExport />
                </TabsContent>
                {isAdmin && (
                    <TabsContent value="sms-suppression" className="mt-6">
                        <SmsSuppressionReport />
                    </TabsContent>
                )}
                <TabsContent value="sms-delivery" className="mt-6 space-y-6" data-testid="tab-content-sms-delivery">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">SMS Delivery &amp; Error Report</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Unified view of every SMS attempt — lead campaigns, workflow automations, and inbound signals.
                        Provider ledger (Twilio) is the source of truth; skipped intents surfaced with reason.
                    </p>
                    {summary?.business_timezone && (
                        <p className="text-[11px] text-muted-foreground mt-1 font-mono" data-testid="tz-note">
                            Today / Yesterday anchored to <span className="font-semibold">{summary.business_timezone}</span>.
                        </p>
                    )}
                </div>
            </div>

            {meta && meta.twilio_status_callback_configured === false && (
                <div
                    className="border border-amber-300 bg-amber-50/70 rounded-xl px-4 py-3 flex items-start gap-3"
                    data-testid="callback-warning"
                >
                    <Info className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                    <div className="text-[13px] text-amber-900 leading-relaxed">
                        <span className="font-semibold">Delivered counts are incomplete.</span>{" "}
                        {meta.notes || (
                            <>CaseFlow captures the initial Twilio accept response only. Terminal statuses
                            (delivered / undelivered) require Twilio status callbacks, which are not wired yet.
                            Use the Twilio Errors page for on-demand carrier reconciliation.</>
                        )}
                    </div>
                </div>
            )}
            {meta && meta.twilio_status_callback_configured === true && (
                <div
                    className={`border rounded-xl px-4 py-3 flex items-start gap-3 ${
                        meta.twilio_status_callback_last_received_at
                            ? "border-emerald-300 bg-emerald-50/70"
                            : "border-amber-300 bg-amber-50/70"
                    }`}
                    data-testid="callback-active-banner"
                    data-callback-state={meta.twilio_status_callback_state}
                >
                    <Info className={`h-4 w-4 mt-0.5 shrink-0 ${meta.twilio_status_callback_last_received_at ? "text-emerald-700" : "text-amber-700"}`} />
                    <div className="text-[13px] leading-relaxed">
                        <span className={`font-semibold ${meta.twilio_status_callback_last_received_at ? "text-emerald-900" : "text-amber-900"}`}>
                            {meta.twilio_status_callback_last_received_at
                                ? "Delivery statuses are updated from Twilio status callbacks."
                                : "Callback URL configured — waiting for first callback."}
                        </span>
                        <div className="mt-1 text-gray-800 flex flex-wrap gap-x-4 gap-y-0.5 text-[12px]">
                            {meta.twilio_status_callback_last_received_at && (
                                <span data-testid="callback-last-ts">
                                    Last callback: {new Date(meta.twilio_status_callback_last_received_at).toLocaleString()}
                                </span>
                            )}
                            {typeof meta.twilio_status_callback_coverage_percent === "number" && (
                                <span data-testid="callback-coverage">
                                    Coverage: <span className="font-semibold">{meta.twilio_status_callback_coverage_percent}%</span>
                                    {" "}({meta.twilio_status_callback_messages_with_callback}/{meta.twilio_status_callback_messages_with_sid} messages)
                                </span>
                            )}
                            {meta.twilio_status_callback_activation_at && (
                                <span data-testid="callback-activation" className="text-muted-foreground">
                                    Messages sent before {new Date(meta.twilio_status_callback_activation_at).toLocaleDateString()} may not contain terminal status.
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-white border border-border rounded-xl p-5 shadow-sm space-y-4" data-testid="reports-filters">
                {/* Quick timeframe buttons */}
                <div className="flex flex-wrap gap-1.5">
                    {TIMEFRAMES.map(t => (
                        <button
                            key={t.v}
                            data-testid={`tf-btn-${t.v}`}
                            onClick={() => setTimeframe(t.v)}
                            className={`px-3 py-1 text-xs rounded-full border transition ${
                                timeframe === t.v
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-white text-gray-700 border-border hover:bg-gray-50"
                            }`}
                        >{t.l}</button>
                    ))}
                </div>

                <SavedViews timeframe={timeframe} source={source} setTimeframe={setTimeframe} setSource={setSource} />
                {timeframe === "overnight" && summary?.range_start ? (
                    <OvernightWindowNote start={summary.range_start} end={summary.range_end} />
                ) : null}

                {timeframe === "custom" && (
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                        <div>
                            <Label>Start</Label>
                            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="reports-start-date" />
                        </div>
                        <div>
                            <Label>End</Label>
                            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="reports-end-date" />
                        </div>
                        <Button
                            onClick={load}
                            disabled={!customDatesValid || loading}
                            data-testid="reports-custom-go"
                            title={!customDatesValid ? "Select a valid start and end date" : "Run custom-range report"}
                        >
                            {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                            GO
                        </Button>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <Label>Source</Label>
                        <Select value={source} onValueChange={setSource}>
                            <SelectTrigger data-testid="filter-source"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {SOURCES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Status</Label>
                        <Select value={statusBucket} onValueChange={setStatusBucket}>
                            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {STATUS_BUCKETS.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Error Code</Label>
                        <Select value={errorCode} onValueChange={setErrorCode}>
                            <SelectTrigger data-testid="filter-error-code"><SelectValue placeholder="All error codes" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All error codes</SelectItem>
                                {options.error_code_groups ? (
                                    <>
                                        {options.error_code_groups.twilio.length > 0 && (
                                            <SelectGroup data-testid="error-code-group-twilio">
                                                <SelectLabel>Twilio error codes</SelectLabel>
                                                {options.error_code_groups.twilio.map(c => <SelectItem key={c} value={c} data-testid={`error-code-option-${c}`}>{c}</SelectItem>)}
                                            </SelectGroup>
                                        )}
                                        {options.error_code_groups.internal.length > 0 && (
                                            <SelectGroup data-testid="error-code-group-internal">
                                                <SelectLabel>Internal / system reasons</SelectLabel>
                                                {options.error_code_groups.internal.map(c => <SelectItem key={c} value={c} data-testid={`error-code-option-${c}`}>{c}</SelectItem>)}
                                            </SelectGroup>
                                        )}
                                    </>
                                ) : (options.error_codes || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <Label>Campaign</Label>
                        <Select value={campaignId} onValueChange={setCampaignId}>
                            <SelectTrigger data-testid="filter-campaign"><SelectValue placeholder="All campaigns" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All campaigns</SelectItem>
                                {(options.campaigns || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Workflow Stage</Label>
                        <Select value={workflowStatusId} onValueChange={setWorkflowStatusId}>
                            <SelectTrigger data-testid="filter-workflow-status"><SelectValue placeholder="All workflow stages" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All workflow stages</SelectItem>
                                {(options.workflow_statuses || []).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Template</Label>
                        <Select value={templateId} onValueChange={setTemplateId}>
                            <SelectTrigger data-testid="filter-template"><SelectValue placeholder="All templates" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All templates</SelectItem>
                                {(options.templates || []).map(t => (
                                    <SelectItem key={t.id} value={t.id}>{t.template_name || t.template_key}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-end">
                    <div className="relative">
                        <Label>Search</Label>
                        <div className="relative">
                            <Search className="h-4 w-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
                            <Input
                                placeholder="Name, phone, case ID, or A+ ID"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="pl-8"
                                data-testid="filter-search"
                            />
                        </div>
                    </div>
                    <Button onClick={load} disabled={loading || (timeframe === "custom" && !customDatesValid)} data-testid="reports-run">
                        {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                        {loading ? "Loading…" : "Run report"}
                    </Button>
                    <Button variant="outline" onClick={downloadCSV} disabled={!rows.length} data-testid="reports-download-csv">
                        <Download className="h-4 w-4 mr-1.5" /> Export CSV
                    </Button>
                </div>
            </div>

            {/* Summary cards */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="summary-cards">
                    <SummaryCard testId="card-total" icon={Send} label="Total Attempts" value={summary.attempted} sub={`${summary.total_rows} total rows`} tone="primary" />
                    <SummaryCard testId="card-delivered" icon={CheckCircle2} label="Delivered (confirmed)" value={summary.delivered} sub={`${deliveryPercent}% of attempted`} tone="emerald" />
                    <SummaryCard testId="card-sent" icon={Zap} label="Sent (in transit)" value={summary.sent} sub="Awaiting delivery receipt" tone="sky" />
                    <SummaryCard testId="card-in-flight" icon={Clock} label="Accepted by Twilio" value={summary.in_flight} sub="Pending delivery status" tone="indigo" />
                    <SummaryCard testId="card-failed" icon={XCircle} label="Failed" value={summary.failed} sub={`${summary.opted_out} opted out`} tone="rose" />
                    <SummaryCard testId="card-skipped" icon={SkipForward} label="Skipped" value={summary.skipped} sub={`${summary.unique_recipients} unique recipients`} tone="slate" />
                </div>
            )}

            {/* Charts */}
            {summary && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="charts-row">
                    <div className="bg-white border border-border rounded-xl p-4 shadow-sm">
                        <div className="text-sm font-medium mb-2">Status breakdown</div>
                        {statusChartData.length === 0 ? (
                            <div className="h-56 flex items-center justify-center text-sm text-muted-foreground">No data in range</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={224}>
                                <PieChart>
                                    <Pie data={statusChartData} dataKey="count" nameKey="name" outerRadius={80} label>
                                        {statusChartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                    <div className="bg-white border border-border rounded-xl p-4 shadow-sm">
                        <div className="text-sm font-medium mb-2">Top error codes</div>
                        {topErrorsData.length === 0 ? (
                            <div className="h-56 flex items-center justify-center text-sm text-muted-foreground">No errors captured in range</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={224}>
                                <BarChart data={topErrorsData} margin={{ top: 10, right: 12, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="code" tick={{ fontSize: 11 }} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            )}

            {summary && dailySeries.length > 0 && (
                <div className="bg-white border border-border rounded-xl p-4 shadow-sm" data-testid="daily-chart">
                    <div className="text-sm font-medium mb-2">Daily delivery vs. failures</div>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={dailySeries} margin={{ top: 10, right: 12, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="delivered" stroke="#10b981" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="sent" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="failed" stroke="#f43f5e" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="opted_out" stroke="#f59e0b" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="skipped" stroke="#64748b" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Table */}
            <div className="bg-white border border-border rounded-xl shadow-sm overflow-hidden" data-testid="reports-results">
                {loading ? (
                    <div className="p-10 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading rows…
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center" data-testid="reports-empty">
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 mb-3">
                            <CheckCircle2 className="h-5 w-5" />
                        </div>
                        <div className="text-base font-medium text-gray-900">No SMS activity in this range.</div>
                        <p className="text-sm text-muted-foreground mt-1">Try widening the timeframe or clearing the filters.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="reports-table">
                            <thead>
                                <tr className="bg-gray-50 text-gray-600 text-[11px] uppercase tracking-wide">
                                    <th className="text-left px-3 py-2 font-medium w-40">Date</th>
                                    <th className="text-left px-3 py-2 font-medium">Status</th>
                                    <th className="text-left px-3 py-2 font-medium">Source</th>
                                    <th className="text-left px-3 py-2 font-medium">Case</th>
                                    <th className="text-left px-3 py-2 font-medium">Recipient</th>
                                    <th className="text-left px-3 py-2 font-medium">Phone</th>
                                    <th className="text-left px-3 py-2 font-medium">Campaign / Workflow</th>
                                    <th className="text-left px-3 py-2 font-medium">Template</th>
                                    <th className="text-left px-3 py-2 font-medium">Error</th>
                                    <th className="text-left px-3 py-2 font-medium">Message</th>
                                    <th className="text-left px-3 py-2 font-medium">Provider Detail</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {rows.slice(0, 500).map((r) => (
                                    <tr key={r.row_key} className="hover:bg-gray-50/60" data-testid="reports-row">
                                        <td className="px-3 py-2 align-top font-mono text-[12.5px] whitespace-nowrap">
                                            {r.created_at
                                                ? (timeframe === "overnight"
                                                    ? `${new Date(r.created_at).toLocaleString("en-US", PT_FMT)} PT`
                                                    : new Date(r.created_at).toLocaleString())
                                                : "—"}
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            {r.direction === "inbound" && r.channel === "sms"
                                                ? <InboundStatus row={r} />
                                                : <StatusPill bucket={r.status_bucket} />}
                                            {r.failure_category ? (
                                                <div className="text-[10px] text-rose-700 mt-1 uppercase tracking-wide">{r.failure_category.replace(/_/g, " ")}</div>
                                            ) : null}
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5">
                                                {r.source === "campaign" ? <Send className="h-3 w-3" /> :
                                                    r.source === "workflow" ? <Zap className="h-3 w-3" /> :
                                                    r.source === "voicemail" ? <Voicemail className="h-3 w-3" /> :
                                                    r.source === "inbound" ? <ChevronRight className="h-3 w-3" /> :
                                                    <AlertTriangle className="h-3 w-3" />}
                                                {r.source}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 align-top font-mono text-[12.5px]">{r.case_id || r.acos_id || "—"}</td>
                                        <td className="px-3 py-2 align-top">
                                            {r.inbound_match_status === "unmatched"
                                                ? <span className="font-medium text-rose-700" data-testid="reports-row-unmatched-name">Unmatched</span>
                                                : (r.lead_name || "—")}
                                        </td>
                                        <td className="px-3 py-2 align-top font-mono text-[12.5px]">
                                            {r.direction === "inbound" && r.from_phone ? (
                                                <div data-testid="reports-row-inbound-phones">
                                                    <div>from {r.from_phone}</div>
                                                    {r.to_phone ? <div className="text-[11px] text-gray-400">to {r.to_phone}</div> : null}
                                                </div>
                                            ) : (r.phone_e164 || "—")}
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            {r.campaign_name ? (
                                                <span className="text-xs">{r.campaign_name}</span>
                                            ) : r.workflow_status_name ? (
                                                <span className="text-xs">{r.workflow_status_name}</span>
                                            ) : "—"}
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            {r.template_name || r.template_key || "—"}
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            {r.error_code ? (
                                                <span className="font-mono text-[12px] bg-rose-50 text-rose-800 px-1.5 py-0.5 rounded">
                                                    {r.error_code}
                                                </span>
                                            ) : null}
                                            {r.error_message ? (
                                                <div className="text-[11px] text-gray-600 mt-0.5 max-w-xs truncate" title={r.error_message}>
                                                    {r.error_message}
                                                </div>
                                            ) : null}
                                        </td>
                                        <td className="px-3 py-2 align-top max-w-sm" data-testid="reports-row-body">
                                            {r.body ? (
                                                <div
                                                    className="text-[12px] text-gray-800 whitespace-pre-wrap break-words bg-gray-50 border-l-2 border-gray-300 rounded-r px-2 py-1"
                                                    title={r.body}
                                                    data-testid="reports-row-body-text"
                                                >
                                                    {r.body}
                                                </div>
                                            ) : (
                                                <span className="text-[11px] text-gray-400">—</span>
                                            )}
                                            {r.source === "voicemail" && r.voicemail_drafter ? (
                                                <DrafterMeta drafter={r.voicemail_drafter} reason={r.voicemail_drafter_reason} />
                                            ) : null}
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            <div className="text-[11px] text-gray-500">
                                                raw: <span className="font-mono">{r.raw_status || "—"}</span>
                                                {r.raw_provider_status ? <> · <span className="font-mono">{r.raw_provider_status}</span></> : null}
                                            </div>
                                            {r.provider_message_id ? (
                                                <div className="font-mono text-[11px] text-gray-400 truncate max-w-[180px]" title={r.provider_message_id}>
                                                    {r.provider_message_id}
                                                </div>
                                            ) : null}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {rows.length > 500 && (
                            <div className="px-3 py-2 text-xs text-muted-foreground border-t">
                                Showing 500 of {rows.length} rows. Export CSV for the full result set.
                            </div>
                        )}
                    </div>
                )}
            </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
